import useScopedLoad from './hooks/useScopedLoad';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import QuantityWithUnit from './components/QuantityWithUnit';
import { listColdRoomReservations } from './api/tumblerApi';
import { formatBangkokDateTime } from './utils/bangkokTime';

// IMPLEMENTATION_PLAN.md Phase 4 + ASSESSMENT.md "Backend dependencies ที่ frontend แก้แทนไม่ได้"
// item 4: there is no authoritative "available barrel stock" aggregate endpoint yet. Rather than
// fabricate an Available/Reserved figure from the reservation ledger (which the assessment
// explicitly calls out as not a valid substitute — "ห้ามแทนด้วย lot ledger"), this view shows
// only the reservations/allocations we do have, links back to the owning scenario/batch, and
// says plainly that the aggregate is not available. Swap in a real "available" column once that
// endpoint exists.
export default function ColdRoomInventory({ plant, factory }) {
  const accessToken = useSelector((state) => state.auth.access_token);


  const loader = useCallback(async (signal) => {
    const response = await listColdRoomReservations({ plant, factory, accessToken, signal });
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  }, [plant, factory, accessToken]);
  const read = useScopedLoad(`${plant}/${factory}`, loader, Boolean(accessToken && plant && factory));
  const state = { ...read, results: read.data || [] };


  if (state.loading && !state.results.length) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 160 }}><CircularProgress size={28} /></Box>;
  }

  if (state.error) {
    return <Alert severity="error">{state.error.summary}</Alert>;
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        This table shows planned cold-room reservations. It does not represent the current physical stock balance.
      </Alert>
      {!state.results.length ? (
        <Typography color="text.secondary">No cold-room reservations for this plant/factory.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', minWidth: 800, borderCollapse: 'collapse', '& th, & td': { p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }, '& th': { textAlign: 'left', fontWeight: 600 } }}>
            <thead>
              <tr>{['Allocation', 'Downstream line', 'Reserved (kg)', 'Planned transfer', 'Planned withdrawal', 'Reserved by (scenario/batch)'].map((label) => <th key={label}>{label}</th>)}</tr>
            </thead>
            <tbody>
              {state.results.map((row, index) => (
                <tr key={row.id ?? index}>
                  <td>{row.barrel_allocation ?? '—'}</td>
                  <td>{row.downstream_line || '—'}</td>
                  <td><QuantityWithUnit value={row.quantity_kg} unit="kg" /></td>
                  <td>{row.planned_transfer_time ? formatBangkokDateTime(row.planned_transfer_time) : '—'}</td>
                  <td>{row.planned_withdrawal_time ? formatBangkokDateTime(row.planned_withdrawal_time) : '—'}</td>
                  <td>{row.scenario ? `Scenario #${row.scenario}` : ''} {row.batch ? `/ Batch #${row.batch}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
}
